import { createElement, useState } from 'react';
import PropTypes from 'prop-types';

import { Box, useTheme } from '@mui/material';
import { TabPanel } from '../tabPanel';

export default function WTabs({
  tabsItems = [],
  handleActiveTabChange = () => {},
}) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const errorColor = theme.palette.error.main;
  const [value, setValue] = useState(tabsItems[0]?.value || 0);

  const handleChange = (index) => {
    setValue(index);
    handleActiveTabChange(tabsItems[index]);
  };

  const count = tabsItems.length || 1;
  const tabWidth = 100 / count;

  return (
    <Box>
      <Box
        role="tablist"
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'row',
          backgroundColor: '#E9E9E9',
          borderRadius: 8,
          p: '4px',
          mb: 2,
        }}
      >
        {/* Sliding indicator */}
        <Box
          sx={{
            position: 'absolute',
            top: '4px',
            bottom: '4px',
            left: '4px',
            width: `calc(${tabWidth}% - 4px)`,
            backgroundColor: primaryColor,
            borderRadius: 7,
            transform: `translateX(calc(${value * 100}% + ${value * 4}px))`,
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            pointerEvents: 'none',
          }}
        />

        {tabsItems.map((tab, index) => {
          const isActive = value === index;
          const showCount = tab.showCount ?? true;
          return (
            <Box
              key={`tab-${tab.id}-${tab.value}`}
              onClick={() => handleChange(index)}
              id={`tab-${index}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${index}`}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleChange(index);
              }}
              sx={{
                flex: 1,
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderRadius: 7,
                py: '8px',
                px: '4px',
                userSelect: 'none',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: showCount ? '4px' : 0,
                  minHeight: 0,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? '#ffffff' : '#333333',
                    lineHeight: 1.2,
                    textAlign: 'center',
                    textTransform: 'capitalize',
                    transition: 'color 0.3s ease',
                  }}
                >
                  {tab.label}
                </Box>
                {showCount && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: 1,
                      minWidth: 20,
                      height: 20,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isActive ? '#ffffff' : errorColor,
                      color: isActive ? primaryColor : '#ffffff',
                      px: '4px',
                      transition: 'background-color 0.3s ease, color 0.3s ease',
                    }}
                  >
                    {tab.count ?? 0}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      {tabsItems.map((tab, index) => (
        <TabPanel
          key={`tabPanel-${tab.id}-${tab.value}`}
          value={value}
          index={index}
          dir={theme.direction}
        >
          {createElement(tab.component, { ...tab })}
        </TabPanel>
      ))}
    </Box>
  );
}

WTabs.propTypes = {
  tabsItems: PropTypes.arrayOf(PropTypes.object).isRequired,
};
